import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  AlertTriangle,
  Clock,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  RefreshCw,
  ChevronLeft,
  Calendar,
  BarChart3,
  Target,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Wrench,
  X,
  Settings,
  Mail,
  Send,
  History,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#6366f1', '#8b5cf6', '#06b6d4'];

export default function SuperAgentReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [activeTab, setActiveTab] = useState('building');
  
  // Report data
  const [buildingReport, setBuildingReport] = useState(null);
  const [agentReport, setAgentReport] = useState(null);
  const [priorityReport, setPriorityReport] = useState(null);
  const [statusReport, setStatusReport] = useState(null);
  const [serviceReport, setServiceReport] = useState(null);
  
  // Filters
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [buildingFilter, setBuildingFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  
  // Buildings and agents for filters
  const [buildings, setBuildings] = useState([]);
  const [agents, setAgents] = useState([]);
  
  // Drilldown
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [drilldownTickets, setDrilldownTickets] = useState(null);
  const [drilldownTitle, setDrilldownTitle] = useState('');
  
  // Schedule modal
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  
  const [exporting, setExporting] = useState(false);
  
  useEffect(() => {
    fetchCompanyId();
  }, []);
  
  useEffect(() => {
    if (companyId) {
      fetchReports();
      fetchFiltersData();
    }
  }, [companyId, dateRange]);
  
  const fetchCompanyId = async () => {
    try {
      const res = await fetch(`${API_URL}/api/agent-company/my-dashboard`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyId(data.company?.company_id);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const fetchFiltersData = async () => {
    try {
      const [buildingsRes, staffRes] = await Promise.all([
        fetch(`${API_URL}/api/agent-company/buildings/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/agent-company/staff/${companyId}`, { credentials: 'include' })
      ]);
      
      if (buildingsRes.ok) setBuildings(await buildingsRes.json());
      if (staffRes.ok) setAgents(await staffRes.json());
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };
  
  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (buildingFilter) params.append('building_filter', buildingFilter);
      if (agentFilter) params.append('agent_filter', agentFilter);
      
      const [buildingRes, agentRes, priorityRes, statusRes, serviceRes] = await Promise.all([
        fetch(`${API_URL}/api/super-reports/building-performance/${companyId}?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-reports/agent-performance/${companyId}?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-reports/priority-breakdown/${companyId}?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-reports/ticket-status/${companyId}?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-reports/building-service/${companyId}?${params}`, { credentials: 'include' })
      ]);
      
      if (buildingRes.ok) setBuildingReport(await buildingRes.json());
      if (agentRes.ok) setAgentReport(await agentRes.json());
      if (priorityRes.ok) setPriorityReport(await priorityRes.json());
      if (statusRes.ok) setStatusReport(await statusRes.json());
      if (serviceRes.ok) setServiceReport(await serviceRes.json());
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDrilldown = async (params, title) => {
    try {
      const queryParams = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
        ...params
      });
      
      const res = await fetch(`${API_URL}/api/super-reports/ticket-drilldown/${companyId}?${queryParams}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setDrilldownTickets(data);
        setDrilldownTitle(title);
        setShowDrilldown(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const handleExport = async (reportType, format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      const res = await fetch(
        `${API_URL}/api/super-reports/export/${companyId}/${reportType}/${format}?${params}`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        const data = await res.json();
        
        if (format === 'csv' || format === 'excel') {
          const blob = new Blob([data.content], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename;
          a.click();
          window.URL.revokeObjectURL(url);
        } else {
          const binary = atob(data.content);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = data.filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        
        toast.success(`Report exported as ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };
  
  const fetchSchedule = async () => {
    try {
      const [scheduleRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/super-reports/schedule/${companyId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/super-reports/history/${companyId}`, { credentials: 'include' })
      ]);
      
      if (scheduleRes.ok) setSchedule(await scheduleRes.json());
      if (historyRes.ok) {
        const data = await historyRes.json();
        setReportHistory(data.history || []);
      }
      setShowSchedule(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };
  
  const handleSaveSchedule = async () => {
    try {
      const res = await fetch(`${API_URL}/api/super-reports/schedule/${companyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(schedule)
      });
      
      if (res.ok) {
        toast.success('Schedule saved');
        setShowSchedule(false);
      }
    } catch (error) {
      toast.error('Failed to save schedule');
    }
  };
  
  const handleSendNow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/super-reports/schedule/${companyId}/send`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Report sent!');
        fetchSchedule();
      }
    } catch (error) {
      toast.error('Failed to send report');
    }
  };
  
  if (loading && !buildingReport) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="super-agent-reports">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/agent/company-management')}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Super Agent Reports</h1>
                <p className="text-sm text-slate-500">Comprehensive operational analytics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={fetchSchedule}>
                <Settings className="w-4 h-4 mr-2" />
                Schedule
              </Button>
              <Button variant="outline" onClick={fetchReports}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="border-slate-200 mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="w-4 h-4 text-indigo-600" />
              Report Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Building</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  value={buildingFilter}
                  onChange={(e) => setBuildingFilter(e.target.value)}
                >
                  <option value="">All Buildings</option>
                  {buildings.map(b => (
                    <option key={b.hoa_id} value={b.hoa_id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">Agent</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                >
                  <option value="">All Agents</option>
                  {agents.map(a => (
                    <option key={a.agent_id} value={a.agent_id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={fetchReports} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 p-1 mb-6">
            <TabsTrigger value="building" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Building2 className="w-4 h-4 mr-2" />
              Building
            </TabsTrigger>
            <TabsTrigger value="agent" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Users className="w-4 h-4 mr-2" />
              Agent
            </TabsTrigger>
            <TabsTrigger value="priority" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Priority
            </TabsTrigger>
            <TabsTrigger value="status" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <Target className="w-4 h-4 mr-2" />
              Status
            </TabsTrigger>
            <TabsTrigger value="service" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
              <TrendingUp className="w-4 h-4 mr-2" />
              Service
            </TabsTrigger>
          </TabsList>
          
          {/* Building Performance Tab */}
          <TabsContent value="building">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Building2 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingReport?.summary?.total_buildings || 0}</p>
                    <p className="text-xs text-slate-500">Buildings</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Wrench className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingReport?.summary?.total_tickets || 0}</p>
                    <p className="text-xs text-slate-500">Total Tickets</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingReport?.summary?.total_resolved || 0}</p>
                    <p className="text-xs text-slate-500">Resolved</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{buildingReport?.summary?.total_open || 0}</p>
                    <p className="text-xs text-slate-500">Open</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Building Table */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Building Performance</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExport('building', 'csv')} disabled={exporting}>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExport('building', 'pdf')} disabled={exporting}>
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Building</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Total</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Resolved</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Open</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">On Hold</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Avg Res (h)</th>
                          <th className="text-center py-3 px-2 font-medium text-red-600">P1</th>
                          <th className="text-center py-3 px-2 font-medium text-amber-600">P2</th>
                          <th className="text-center py-3 px-2 font-medium text-emerald-600">P3</th>
                          <th className="text-right py-3 px-2 font-medium text-slate-600">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildingReport?.buildings?.map((b) => (
                          <tr key={b.hoa_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2">
                              <p className="font-medium">{b.building_name}</p>
                              <p className="text-xs text-slate-500">{b.units} units</p>
                            </td>
                            <td className="text-center py-3 px-2 font-semibold">{b.total_tickets}</td>
                            <td className="text-center py-3 px-2 text-emerald-600">{b.resolved_tickets}</td>
                            <td className="text-center py-3 px-2 text-amber-600">{b.open_tickets}</td>
                            <td className="text-center py-3 px-2 text-slate-500">{b.on_hold_tickets}</td>
                            <td className="text-center py-3 px-2">{b.avg_resolution_hours}h</td>
                            <td className="text-center py-3 px-2">
                              <Badge variant="destructive" className="text-xs">{b.p1_tickets}</Badge>
                            </td>
                            <td className="text-center py-3 px-2">
                              <Badge variant="default" className="bg-amber-500 text-xs">{b.p2_tickets}</Badge>
                            </td>
                            <td className="text-center py-3 px-2">
                              <Badge variant="secondary" className="text-xs">{b.p3_tickets}</Badge>
                            </td>
                            <td className="text-right py-3 px-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => fetchDrilldown({ building_id: b.hoa_id }, `Tickets: ${b.building_name}`)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Agent Performance Tab */}
          <TabsContent value="agent">
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{agentReport?.summary?.total_agents || 0}</p>
                    <p className="text-xs text-slate-500">Agents</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Wrench className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{agentReport?.summary?.total_tickets_handled || 0}</p>
                    <p className="text-xs text-slate-500">Tickets Handled</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{agentReport?.summary?.total_resolved || 0}</p>
                    <p className="text-xs text-slate-500">Resolved</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{agentReport?.summary?.avg_resolution_rate || 0}%</p>
                    <p className="text-xs text-slate-500">Avg Resolution Rate</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Agent Table */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Agent Performance</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExport('agent', 'csv')} disabled={exporting}>
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExport('agent', 'pdf')} disabled={exporting}>
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2 font-medium text-slate-600">Agent</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Buildings</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Total</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Resolved</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Open</th>
                          <th className="text-center py-3 px-2 font-medium text-slate-600">Rate %</th>
                          <th className="text-center py-3 px-2 font-medium text-red-600">P1</th>
                          <th className="text-center py-3 px-2 font-medium text-amber-600">P2</th>
                          <th className="text-center py-3 px-2 font-medium text-emerald-600">P3</th>
                          <th className="text-right py-3 px-2 font-medium text-slate-600">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agentReport?.agents?.map((a) => (
                          <tr key={a.agent_id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                  a.role === 'super_agent' ? 'bg-purple-500' : 'bg-blue-500'
                                }`}>
                                  {a.agent_name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                  <p className="font-medium">{a.agent_name}</p>
                                  <p className="text-xs text-slate-500">{a.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="text-center py-3 px-2">{a.building_count}</td>
                            <td className="text-center py-3 px-2 font-semibold">{a.total_tickets}</td>
                            <td className="text-center py-3 px-2 text-emerald-600">{a.resolved_tickets}</td>
                            <td className="text-center py-3 px-2 text-amber-600">{a.open_tickets}</td>
                            <td className="text-center py-3 px-2">
                              <Badge variant={a.resolution_rate >= 80 ? 'default' : 'secondary'} className={
                                a.resolution_rate >= 80 ? 'bg-emerald-500' : ''
                              }>
                                {a.resolution_rate}%
                              </Badge>
                            </td>
                            <td className="text-center py-3 px-2">
                              <Badge variant="destructive" className="text-xs">{a.p1_tickets}</Badge>
                            </td>
                            <td className="text-center py-3 px-2">
                              <Badge variant="default" className="bg-amber-500 text-xs">{a.p2_tickets}</Badge>
                            </td>
                            <td className="text-center py-3 px-2">
                              <Badge variant="secondary" className="text-xs">{a.p3_tickets}</Badge>
                            </td>
                            <td className="text-right py-3 px-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => fetchDrilldown({ agent_id: a.agent_id }, `Tickets: ${a.agent_name}`)}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Priority Breakdown Tab */}
          <TabsContent value="priority">
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie Chart */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Priority Distribution</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => handleExport('priority', 'pdf')} disabled={exporting}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={priorityReport?.distribution || []}
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            dataKey="value"
                            nameKey="priority"
                            label={({ priority, percentage }) => `${priority}: ${percentage}%`}
                            onClick={(data) => fetchDrilldown({ priority: data.priority.includes('P1') ? 'high' : data.priority.includes('P2') ? 'medium' : 'low' }, `${data.priority} Tickets`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {(priorityReport?.distribution || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {/* Priority Legend */}
                    <div className="mt-4 space-y-2">
                      {priorityReport?.distribution?.map((p, idx) => (
                        <div key={p.priority} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                            <span className="font-medium text-sm">{p.priority}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold">{p.value}</span>
                            <span className="text-slate-500 text-sm ml-2">({p.percentage}%)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Building Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Priority by Building</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {priorityReport?.building_breakdown?.map((b) => (
                        <div key={b.hoa_id} className="p-3 bg-slate-50 rounded-lg">
                          <p className="font-medium mb-2">{b.building_name}</p>
                          <div className="flex gap-2">
                            <Badge variant="destructive">P1: {b.p1}</Badge>
                            <Badge className="bg-amber-500">P2: {b.p2}</Badge>
                            <Badge variant="secondary">P3: {b.p3}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Priority Descriptions */}
              <Card>
                <CardHeader>
                  <CardTitle>Priority Definitions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border-l-4 border-red-500 bg-red-50 rounded-r-lg">
                      <h4 className="font-bold text-red-800">P1 - Critical / Emergency</h4>
                      <p className="text-sm text-red-700 mt-1">Burst pipes, electrical faults, security issues</p>
                      <p className="text-xs text-red-600 mt-2">SLA: 24 hours</p>
                    </div>
                    <div className="p-4 border-l-4 border-amber-500 bg-amber-50 rounded-r-lg">
                      <h4 className="font-bold text-amber-800">P2 - Urgent Maintenance</h4>
                      <p className="text-sm text-amber-700 mt-1">Plumbing issues, gate faults, lighting failures</p>
                      <p className="text-xs text-amber-600 mt-2">SLA: 72 hours</p>
                    </div>
                    <div className="p-4 border-l-4 border-emerald-500 bg-emerald-50 rounded-r-lg">
                      <h4 className="font-bold text-emerald-800">P3 - General Maintenance</h4>
                      <p className="text-sm text-emerald-700 mt-1">Minor repairs, landscaping issues</p>
                      <p className="text-xs text-emerald-600 mt-2">SLA: 7 days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Status Tab */}
          <TabsContent value="status">
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Wrench className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{statusReport?.summary?.opened || 0}</p>
                    <p className="text-xs text-slate-500">Opened</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{statusReport?.summary?.resolved || 0}</p>
                    <p className="text-xs text-slate-500">Resolved</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{statusReport?.summary?.outstanding || 0}</p>
                    <p className="text-xs text-slate-500">Outstanding</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold">{statusReport?.summary?.on_hold || 0}</p>
                    <p className="text-xs text-slate-500">On Hold</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status Pie Chart */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Status Distribution</CardTitle>
                    <Button variant="outline" size="sm" onClick={() => handleExport('status', 'pdf')} disabled={exporting}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusReport?.status_distribution || []}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            dataKey="value"
                            nameKey="status"
                            label
                            onClick={(data) => fetchDrilldown({ status: data.status }, `Status: ${data.status}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            {(statusReport?.status_distribution || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Building Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Status by Building</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {statusReport?.building_breakdown?.map((b) => (
                        <div 
                          key={b.hoa_id} 
                          className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                          onClick={() => fetchDrilldown({ building_id: b.hoa_id }, `Tickets: ${b.building_name}`)}
                        >
                          <p className="font-medium mb-2">{b.building_name}</p>
                          <div className="grid grid-cols-4 gap-2 text-center text-xs">
                            <div>
                              <p className="font-bold text-blue-600">{b.opened}</p>
                              <p className="text-slate-500">Opened</p>
                            </div>
                            <div>
                              <p className="font-bold text-emerald-600">{b.resolved}</p>
                              <p className="text-slate-500">Resolved</p>
                            </div>
                            <div>
                              <p className="font-bold text-amber-600">{b.outstanding}</p>
                              <p className="text-slate-500">Outstanding</p>
                            </div>
                            <div>
                              <p className="font-bold text-slate-600">{b.on_hold}</p>
                              <p className="text-slate-500">On Hold</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          
          {/* Service Performance Tab */}
          <TabsContent value="service">
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4 text-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-amber-800">{serviceReport?.summary?.buildings_with_issues || 0}</p>
                    <p className="text-xs text-amber-600">Buildings with Issues</p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-800">{serviceReport?.summary?.total_overdue || 0}</p>
                    <p className="text-xs text-red-600">Total Overdue</p>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="p-4 text-center">
                    <AlertCircle className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-purple-800">{serviceReport?.summary?.total_sla_breaches || 0}</p>
                    <p className="text-xs text-purple-600">SLA Breaches</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Building Service Cards */}
              <Card>
                <CardHeader>
                  <CardTitle>Building Service Performance</CardTitle>
                  <CardDescription>Buildings sorted by service score (lowest first)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {serviceReport?.buildings?.map((b) => (
                      <div 
                        key={b.hoa_id}
                        className={`p-4 rounded-xl border ${
                          b.service_score < 60 ? 'border-red-200 bg-red-50' :
                          b.service_score < 80 ? 'border-amber-200 bg-amber-50' :
                          'border-emerald-200 bg-emerald-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold">{b.building_name}</h4>
                          <Badge variant={
                            b.service_score >= 80 ? 'default' :
                            b.service_score >= 60 ? 'secondary' : 'destructive'
                          } className={b.service_score >= 80 ? 'bg-emerald-500' : ''}>
                            Score: {b.service_score}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-slate-500">Avg Resolution</p>
                            <p className="font-medium">{b.avg_resolution_hours}h</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Overdue</p>
                            <p className={`font-medium ${b.overdue_tickets > 0 ? 'text-red-600' : ''}`}>
                              {b.overdue_tickets}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-500">Pending Contractor</p>
                            <p className="font-medium">{b.pending_contractor}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">SLA Breaches</p>
                            <p className={`font-medium ${b.sla_breaches > 0 ? 'text-red-600' : ''}`}>
                              {b.sla_breaches}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Drilldown Modal */}
      {showDrilldown && drilldownTickets && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{drilldownTitle}</CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowDrilldown(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <CardDescription>{drilldownTickets.total} tickets found</CardDescription>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {drilldownTickets.tickets?.map((ticket) => (
                  <div key={ticket.ticket_id} className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold">{ticket.title}</p>
                        <p className="text-sm text-slate-500">{ticket.building_name}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={
                          ticket.priority === 'high' ? 'destructive' :
                          ticket.priority === 'medium' ? 'default' : 'secondary'
                        }>
                          {ticket.priority === 'high' ? 'P1' : ticket.priority === 'medium' ? 'P2' : 'P3'}
                        </Badge>
                        <Badge variant="outline">{ticket.status}</Badge>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-4 text-xs text-slate-500">
                      <div>
                        <span className="font-medium">Created:</span> {new Date(ticket.created_at).toLocaleDateString()}
                      </div>
                      {ticket.contractor_name && (
                        <div>
                          <span className="font-medium">Contractor:</span> {ticket.contractor_name}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Category:</span> {ticket.category || 'General'}
                      </div>
                    </div>
                  </div>
                ))}
                {drilldownTickets.tickets?.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No tickets found</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Schedule Modal */}
      {showSchedule && schedule && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                  Automated Reports
                </CardTitle>
                <Button variant="ghost" size="icon" onClick={() => setShowSchedule(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="font-medium">Enable Automated Reports</span>
                <Switch
                  checked={schedule.enabled}
                  onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              
              <div>
                <Label>Frequency</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  value={schedule.frequency}
                  onChange={(e) => setSchedule(prev => ({ ...prev, frequency: e.target.value }))}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              {schedule.frequency === 'weekly' ? (
                <div>
                  <Label>Day of Week</Label>
                  <select
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    value={schedule.day_of_week}
                    onChange={(e) => setSchedule(prev => ({ ...prev, day_of_week: parseInt(e.target.value) }))}
                  >
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                  </select>
                </div>
              ) : (
                <div>
                  <Label>Day of Month</Label>
                  <select
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    value={schedule.day_of_month}
                    onChange={(e) => setSchedule(prev => ({ ...prev, day_of_month: parseInt(e.target.value) }))}
                  >
                    {[1, 5, 10, 15, 20, 25].map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div>
                <Label>Reports to Include</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {['building', 'agent', 'priority', 'status'].map(r => (
                    <label key={r} className="flex items-center gap-2 p-2 bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={schedule.reports?.includes(r)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSchedule(prev => ({ ...prev, reports: [...(prev.reports || []), r] }));
                          } else {
                            setSchedule(prev => ({ ...prev, reports: (prev.reports || []).filter(x => x !== r) }));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm capitalize">{r}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm">Include Trustees</span>
                <Switch
                  checked={schedule.include_trustees}
                  onCheckedChange={(checked) => setSchedule(prev => ({ ...prev, include_trustees: checked }))}
                />
              </div>
              
              {/* Report History */}
              {reportHistory.length > 0 && (
                <div>
                  <Label className="flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Recent Reports
                  </Label>
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {reportHistory.map((h, idx) => (
                      <div key={idx} className="text-xs text-slate-500 p-2 bg-slate-50 rounded">
                        {new Date(h.sent_at).toLocaleString()} - {h.reports_included?.join(', ')}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={handleSendNow} className="flex-1">
                  <Send className="w-4 h-4 mr-2" />
                  Send Now
                </Button>
                <Button onClick={handleSaveSchedule} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  Save Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
